import React, { useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://api.insightos.co.uk').replace(/\/$/, '');
const SALES_MEETING_ENDPOINT = `${API_BASE}/sales/addmeeting`;
const DURATION_OPTIONS = [15, 30, 45, 60, 90];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const normalizeEmail = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim().toLowerCase();
};

export default function CreateMeeting() {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const leadContext = location.state || {};

  const defaultLeadLabel = leadContext.leadLabel || 'Prospect';
  const defaultMeetingName = leadContext.company ? `Demo with ${leadContext.company}` : `Demo with ${defaultLeadLabel}`;
  const defaultAttendee = normalizeEmail(leadContext.attendeeEmail || '');

  const [meetingName, setMeetingName] = useState(defaultMeetingName);
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingLength, setMeetingLength] = useState('30');
  const [notes, setNotes] = useState('');
  const [attendees, setAttendees] = useState(() => (defaultAttendee ? [defaultAttendee] : []));
  const [newAttendee, setNewAttendee] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const availableOfficerEmails = useMemo(() => {
    const officerEmails = Array.isArray(leadContext.officerEmails) ? leadContext.officerEmails : [];
    return officerEmails
      .map(normalizeEmail)
      .filter((email) => email && !attendees.includes(email));
  }, [attendees, leadContext.officerEmails]);

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#05021b] px-4 py-16 text-center text-white">
        <p className="text-lg font-semibold">Please log in to schedule demos.</p>
      </div>
    );
  }

  const addAttendee = (email) => {
    const normalized = normalizeEmail(email);
    if (!normalized || !EMAIL_REGEX.test(normalized)) {
      setError('Enter a valid attendee email address.');
      return;
    }
    if (!attendees.includes(normalized)) {
      setAttendees((prev) => [...prev, normalized]);
    }
    setNewAttendee('');
    setError('');
  };

  const removeAttendee = (email) => {
    setAttendees((prev) => prev.filter((entry) => entry !== email));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!meetingDate || !meetingTime) {
      setError('Date and time are required.');
      return;
    }
    if (!attendees.length) {
      setError('Add at least one attendee email.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        meetingName: meetingName.trim() || defaultMeetingName,
        meetingDate,
        meetingTime,
        meetingLength: Number(meetingLength) || 30,
        attendees,
        notes: notes.trim(),
        leadId: leadContext.leadId || null,
        leadLabel: defaultLeadLabel,
      };

      const response = await fetch(SALES_MEETING_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to schedule the meeting.');
      }

      setSuccessMessage('Demo meeting scheduled. Redirecting to dashboard…');
      setTimeout(() => {
        navigate('/sales-dashboard', {
          state: { meetingSuccess: 'Demo meeting booked and Google Meet invite sent.' },
          replace: true,
        });
      }, 900);
    } catch (submitError) {
      console.error(submitError);
      setError(submitError.message || 'Unable to create the meeting.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05021b] px-4 py-10 text-white sm:px-6 lg:px-10">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <header className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#1b1a4b] via-[#0c0a2b] to-[#070312] p-8 shadow-[0_25px_140px_rgba(15,6,45,0.7)]">
          <p className="text-xs uppercase tracking-[0.4em] text-sky-300">Sales demo</p>
          <h1 className="mt-3 text-3xl font-semibold text-white">Schedule a Google Meet demo</h1>
          <p className="mt-2 text-sm text-slate-200">
            Pre-fill the attendee from your lead's officer list, pick a slot, and we'll spin up the Meet link automatically.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="rounded-3xl border border-white/10 bg-[#050d1d] p-6 shadow-2xl">
          <div className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Meeting name</label>
              <input
                type="text"
                value={meetingName}
                onChange={(event) => setMeetingName(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-white/10 bg-white/90 px-4 py-3 text-slate-900 focus:border-indigo-400 focus:outline-none"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Date</label>
                <input
                  type="date"
                  value={meetingDate}
                  onChange={(event) => setMeetingDate(event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/90 px-4 py-3 text-slate-900 focus:border-indigo-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Time</label>
                <input
                  type="time"
                  value={meetingTime}
                  onChange={(event) => setMeetingTime(event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/90 px-4 py-3 text-slate-900 focus:border-indigo-400 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Duration</label>
              <select
                value={meetingLength}
                onChange={(event) => setMeetingLength(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-white/10 bg-white/90 px-4 py-3 text-slate-900 focus:border-indigo-400 focus:outline-none"
              >
                {DURATION_OPTIONS.map((minutes) => (
                  <option key={minutes} value={minutes}>{`${minutes} min`}</option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Attendees</p>
                  <p className="text-xs text-slate-500">Invite founders or directors to receive the Meet link.</p>
                </div>
                {availableOfficerEmails.length > 0 && (
                  <button
                    type="button"
                    onClick={() => addAttendee(availableOfficerEmails[0])}
                    className="rounded-full border border-white/20 px-3 py-1 text-xs text-slate-200 hover:border-indigo-200/60"
                  >
                    Add first officer
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {attendees.map((email) => (
                  <span key={email} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs text-white">
                    {email}
                    <button type="button" className="text-rose-200" onClick={() => removeAttendee(email)}>
                      ×
                    </button>
                  </span>
                ))}
                {attendees.length === 0 && <p className="text-xs text-slate-500">No attendees added yet.</p>}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  value={newAttendee}
                  onChange={(event) => setNewAttendee(event.target.value)}
                  placeholder="founder@company.com"
                  className="flex-1 rounded-2xl border border-white/10 bg-white/90 px-4 py-3 text-slate-900 focus:border-indigo-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => addAttendee(newAttendee)}
                  className="rounded-2xl bg-gradient-to-r from-emerald-400 to-cyan-400 px-4 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-emerald-500/40"
                >
                  Add attendee
                </button>
              </div>
              {availableOfficerEmails.length > 1 && (
                <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                  <p>Quick add:</p>
                  {availableOfficerEmails.map((email) => (
                    <button
                      key={email}
                      type="button"
                      onClick={() => addAttendee(email)}
                      className="rounded-full border border-white/15 px-3 py-1 text-slate-200 hover:border-indigo-200/60"
                    >
                      {email}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Agenda / notes</label>
              <textarea
                rows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-white/10 bg-white/90 px-4 py-3 text-slate-900 focus:border-indigo-400 focus:outline-none"
              />
            </div>

            {error && <p className="text-sm text-rose-300">{error}</p>}
            {successMessage && <p className="text-sm text-emerald-300">{successMessage}</p>}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-400 px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-purple-500/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Scheduling…' : 'Send demo invite'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales-dashboard')}
                className="rounded-full border border-white/20 px-6 py-3 text-sm text-slate-200 hover:border-indigo-200/60"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
